import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Separator } from "../ui/separator";
import { toast } from "sonner";
import {
  FileText, Download, Copy, BookOpen, Image, Video,
  Mail, MessageSquare, Phone, Presentation, ExternalLink,
  ChevronRight, Sparkles
} from "lucide-react";
import { API } from "../../apiConfig";


const SalesToolkit = ({ token }) => {
  const [scripts, setScripts] = useState(null);
  const [materials, setMaterials] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeScript, setActiveScript] = useState("introduction");

  useEffect(() => {
    fetchToolkitData();
  }, []);

  const fetchToolkitData = async () => {
    try {
      const [scriptsRes, materialsRes, templatesRes] = await Promise.all([
        axios.get(`${API}/sales-agent/toolkit/sales-scripts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/sales-agent/toolkit/marketing-materials`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/sales-agent/email-templates`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setScripts(scriptsRes.data);
      setMaterials(materialsRes.data);
      setTemplates(templatesRes.data.templates || []);
    } catch (error) {
      console.error("Error fetching toolkit:", error);
      toast.error("Failed to load toolkit");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const downloadPriceList = async () => {
    try {
      const response = await axios.get(`${API}/sales-agent/toolkit/price-list?format=csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'arawol_price_list.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Price list downloaded");
    } catch (error) {
      toast.error("Failed to download price list");
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Loading toolkit...</div>;
  }

  return (
    <div className="space-y-6">
      <Tabs defaultValue="scripts" className="space-y-4">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="scripts" className="gap-2">
            <MessageSquare className="w-4 h-4" /> Scripts
          </TabsTrigger>
          <TabsTrigger value="templates" className="gap-2">
            <Mail className="w-4 h-4" /> Email Templates
          </TabsTrigger>
          <TabsTrigger value="materials" className="gap-2">
            <FileText className="w-4 h-4" /> Materials
          </TabsTrigger>
          <TabsTrigger value="downloads" className="gap-2">
            <Download className="w-4 h-4" /> Downloads
          </TabsTrigger>
        </TabsList>

        {/* Sales Scripts */}
        <TabsContent value="scripts" className="space-y-4">
          <Card className="rounded-2xl">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Sales Scripts & Talking Points</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  {scripts && Object.keys(scripts).map((key) => (
                    <Button
                      key={key}
                      variant={activeScript === key ? "default" : "ghost"}
                      className="w-full justify-start"
                      onClick={() => setActiveScript(key)}
                    >
                      {scripts[key].title}
                    </Button>
                  ))}
                </div>
                <div className="md:col-span-3">
                  {scripts && scripts[activeScript] && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-lg">{scripts[activeScript].title}</h3>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyToClipboard(scripts[activeScript].script || JSON.stringify(scripts[activeScript].responses, null, 2), "Script")}
                        >
                          <Copy className="w-4 h-4 mr-2" /> Copy
                        </Button>
                      </div>
                      
                      {scripts[activeScript].script && (
                        <div className="p-4 bg-muted/50 rounded-xl whitespace-pre-wrap text-sm">
                          {scripts[activeScript].script}
                        </div>
                      )}

                      {scripts[activeScript].responses && (
                        <div className="space-y-3">
                          {Object.entries(scripts[activeScript].responses).map(([objection, response]) => (
                            <div key={objection} className="p-3 bg-muted/30 rounded-lg">
                              <p className="font-medium text-sm text-destructive mb-1">"{objection}"</p>
                              <p className="text-sm">{response}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {scripts[activeScript].tips && (
                        <div className="p-4 bg-primary/5 rounded-xl">
                          <p className="font-medium text-sm mb-2 flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-primary" /> Pro Tips
                          </p>
                          <ul className="space-y-1">
                            {scripts[activeScript].tips.map((tip, i) => (
                              <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                                <ChevronRight className="w-4 h-4 mt-0.5 text-primary" />
                                {tip}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Templates */}
        <TabsContent value="templates" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {templates.map((template) => (
              <Card key={template.template_id} className="rounded-2xl">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{template.name}</CardTitle>
                    <Badge variant="outline">{template.category}</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Subject:</p>
                      <p className="text-sm font-medium">{template.subject}</p>
                    </div>
                    <div className="p-3 bg-muted/50 rounded-lg max-h-40 overflow-y-auto">
                      <p className="text-xs whitespace-pre-wrap">{template.body}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => copyToClipboard(template.subject, "Subject")}
                      >
                        <Copy className="w-3 h-3 mr-1" /> Subject
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => copyToClipboard(template.body, "Body")}
                      >
                        <Copy className="w-3 h-3 mr-1" /> Body
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Marketing Materials */}
        <TabsContent value="materials" className="space-y-4">
          {materials && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="rounded-2xl">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Image className="w-4 h-4" /> Brand Assets
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {materials.brand_assets?.map((asset, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
                        <div>
                          <p className="text-sm font-medium">{asset.name}</p>
                          <p className="text-xs text-muted-foreground">{asset.format}</p>
                        </div>
                        <Button variant="ghost" size="sm">
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="rounded-2xl">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Print Materials
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {materials.print_materials?.map((item, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
                        <div>
                          <p className="text-sm font-medium">{item.name}</p>
                          <p className="text-xs text-muted-foreground">{item.description}</p>
                        </div>
                        <Button variant="ghost" size="sm">
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="rounded-2xl">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Video className="w-4 h-4" /> Videos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {materials.videos?.map((video, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
                        <div>
                          <p className="text-sm font-medium">{video.name}</p>
                          <p className="text-xs text-muted-foreground">{video.duration} • {video.description}</p>
                        </div>
                        <Button variant="ghost" size="sm">
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="rounded-2xl">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Presentation className="w-4 h-4" /> Digital Assets
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {materials.digital_assets?.map((asset, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
                        <div>
                          <p className="text-sm font-medium">{asset.name}</p>
                          <p className="text-xs text-muted-foreground">{asset.description}</p>
                        </div>
                        <Button variant="ghost" size="sm">
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Downloads */}
        <TabsContent value="downloads" className="space-y-4">
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="text-base">Quick Downloads</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Button
                  variant="outline"
                  className="h-24 flex-col gap-2"
                  onClick={downloadPriceList}
                >
                  <FileText className="w-8 h-8" />
                  <span>Price List (CSV)</span>
                </Button>
                <Button variant="outline" className="h-24 flex-col gap-2">
                  <BookOpen className="w-8 h-8" />
                  <span>Product Catalog (PDF)</span>
                </Button>
                <Button variant="outline" className="h-24 flex-col gap-2">
                  <Image className="w-8 h-8" />
                  <span>Product Images (ZIP)</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SalesToolkit;
